import { useQueryClient } from '@tanstack/react-query';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { cancelCartQuery, getCartCache, updateCart } from '@telialt/core-eshop/src/helpers/cartQueryUtils';
import {
    resetToPreviousCartState,
    useMutationWithCartRefresh,
} from '@telialt/core-eshop/src/hooks/mutations/useMutationWithCartRefresh';
import { useCartQuery } from '@telialt/core-eshop/src/hooks/queries/useCartQuery';
import { ESHOP_MUTATION_KEY } from '@telialt/core-eshop/src/model';
import { apiUpdateContractType } from '@telialt/core-eshop/src/store/api';
import { ContractType } from '@telialt/models/src/EshopModel';
import { useLogErrorEshop } from '@telialt/utils/src/datadog/useLogError';
import { useSegment } from '@telialt/utils/src/segment';

export const useMutateContractType = () => {
    const segment = useSegment();
    const { t } = useTranslations();
    const queryClient = useQueryClient();
    const { cart, cartId } = useCartQuery();

    const { mutate, isPending, error } = useMutationWithCartRefresh(
        async (contractType: ContractType) => (await apiUpdateContractType(segment, cartId, contractType)).data,
        {
            mutationKey: [ESHOP_MUTATION_KEY.UPDATE_CONTRACT_TYPE],
            onMutate: async (contractType: ContractType) => {
                await cancelCartQuery(queryClient);
                const cartCache = getCartCache(queryClient);
                updateCart(queryClient, { ...cart, contractType });

                return cartCache;
            },
            onError: resetToPreviousCartState(queryClient, t['eshop.product_page.action_error']),
        },
    );
    useLogErrorEshop(error);

    return {
        mutateContractType: mutate,
        isLoadingMutateContractType: isPending,
        errorMutateContractType: error,
    };
};
